/* global initWebchat, Sentry, DD_LOGS, DD_RUM */
/*
 ** This file contains custom mechanics and for the embedding of the Cognigy V3 webchat widget
 ** Find the official documentation here: https://github.com/Cognigy/Webchat/
 */

const SUPPORTED_NSAPP_SCENARIOS = new Set(['0', '1']);

export default class CognigyWebchat {
  static THEMES = {
    sinterklaas: {
      startDate: new Date(Date.parse('2025-12-01T00:00:00.000+01:00')),
      endDate: new Date(Date.parse('2025-12-06T00:00:00.000+01:00'))
    },
    winter: {
      startDate: new Date(Date.parse('2025-12-12T00:00:00.000+01:00')),
      endDate: new Date(Date.parse('2025-12-27T00:00:00.000+01:00'))
    },
    pasen: {
      startDate: new Date(Date.parse('2026-04-05T00:00:00.000+02:00')),
      endDate: new Date(Date.parse('2026-04-07T00:00:00.000+02:00'))
    },
    koningsdag: {
      startDate: new Date(Date.parse('2026-04-27T00:00:00.000+02:00')),
      endDate: new Date(Date.parse('2026-04-28T00:00:00.000+02:00'))
    },
    pride: {
      startDate: new Date(Date.parse('2026-08-01T00:00:00.000+02:00')),
      endDate: new Date(Date.parse('2026-08-02T00:00:00.000+02:00'))
    }
  };

  getBaseAssetUrl() {
    return '';
  }

  #assets = {
    logoUrl: `${this.getBaseAssetUrl()}/assets/icons/jens.svg`,
    botLogoUrl: `${this.getBaseAssetUrl()}/assets/icons/jens.svg`,
    agentLogoUrl: `${this.getBaseAssetUrl()}/assets/icons/agent.svg`
  };

  #userId;
  #sessionId;
  #lastMessageDate;
  #lastUpdate;
  #initialized;
  #initializing;
  #webchat;
  #language;
  #localization;
  #nsAppScenarioId;
  #startupBehavior;
  #openRequested;
  #webchatStatelisteners = [];
  #visibilityListener = () => this.#handleVisibilityChange();
  #windowFocusListener = () => this.#handleWindowFocus();

  #patchChatUI = () => {
    const headerbar = this.#getHeaderbar();
    if (headerbar) {
      this.#manageFeedbackButton(headerbar);
      this.manageHeaderButtons();
    }
    this.#replaceAttachmentLabels();
    this.#removeScrollButtonContent();
  };

  #observer = new MutationObserver((mutations) => {
    if (!mutations.some((mutation) => mutation.addedNodes.length !== 0)) return;
    this.#patchChatUI();
  });

  #feedbackHandler = async () => {
    await this.openFeedback();
  };

  isFullscreenDevice() {
    return window.matchMedia('(max-width: 575px)').matches;
  }

  isActive() {
    const active =
      localStorage.getItem('jens_chatOpen') === 'true' ||
      localStorage.getItem('jens_handover') === 'true' ||
      localStorage.getItem('jens_minimized') === 'true';

    if (!active) {
      return false;
    }

    const lastMessageDate = new Date(localStorage.getItem('jens_lastMessageDate'));
    const hours = Math.abs(Date.now() - lastMessageDate.getTime()) / 36e5;
    const inHandover = localStorage.getItem('jens_handover') === 'true';
    //users are considered inactive after 30 mins as standard, during handover this extends to 24 hours
    //this accommodates users who request handover just before the end of the agents' work day, who pick up the ticket the following morning
    const threshold = inHandover ? 24 : 0.5;
    return hours <= threshold;
  }

  //Defines the behavior of the webchat after it is loaded on the page
  defineStartupBehavior() {
    throw new Error('defineStartupBehavior() must be implemented in the child class');
  }

  getNSPlatformName() {
    throw new Error('getPlatformName() must be implemented in the child class');
  }

  async #fetchLanguageResources(lang) {
    const response = await fetch(`${this.getBaseAssetUrl()}/lang/${lang}.json`);
    return await response.json();
  }

  #determineNSAppScenarioId() {
    const nsAppReferrerParam = new URLSearchParams(location.search).get('nsaj');
    if (SUPPORTED_NSAPP_SCENARIOS.has(nsAppReferrerParam)) {
      this.#nsAppScenarioId = nsAppReferrerParam;
    }
  }

  #getAppScenarioMessage() {
    if (
      this.#nsAppScenarioId !== undefined &&
      this.#nsAppScenarioId in this.#localization.initialMessageFromMobileApp
    ) {
      return this.#localization.initialMessageFromMobileApp[this.#nsAppScenarioId];
    }
    return null;
  }

  #getStartBehavior(initialMessage) {
    // This is the input shown in the UI as a user input. If it's an empty string, nothing will be shown
    let getStartedText = '';
    // This is what is sent to Cognigy to trigger the chat
    let getStartedPayload = 'GET_STARTED';

    // This is populated when nsaj URL param is used
    let appScenarioMessage = this.#getAppScenarioMessage();

    // The initial message takes precedence over a nsaj message.
    // Both being available can happen when the user came from the app with nsaj=1, then closed the chat and then started a new chat with a new input
    if (typeof initialMessage === 'string' && initialMessage.trim().length > 0) {
      getStartedText = initialMessage;
      getStartedPayload = initialMessage;
    } else if (appScenarioMessage) {
      getStartedText = appScenarioMessage;
      getStartedPayload = appScenarioMessage;
    }

    return {
      startBehavior: 'injection',
      getStartedPayload: getStartedPayload,
      getStartedText: getStartedText,
      getStartedData: {
        ...(this.#nsAppScenarioId === undefined ? {} : { referrer: 'ns-app' }),
        language: this.#language,
        url: window.location.pathname,
        nsplatform: this.getNSPlatformName()
      }
    };
  }

  #updateLocalization(newLang) {
    this.#language = newLang;
    localStorage.setItem('jens_language', newLang);
    this.#fetchLanguageResources(newLang).then((localization) => {
      this.#localization = localization;
      this.#webchat.updateSettings({
        layout: {
          title: localization.botTitle,
          botAvatarName: localization.botAvatarName,
          agentAvatarName: localization.agentAvatarName
        },
        behavior: {
          inputPlaceholder: localization.inputPlaceholder,
          AIAgentNoticeText: localization.AIAgentNoticeText
        },
        privacyNotice: localization.privacyNotice
      });
    });
  }

  #handleVisibilityChange() {
    if (document.visibilityState !== 'visible') return;
    this.#checkForUpdates();
  }

  #handleWindowFocus() {
    this.#checkForUpdates();
  }

  #checkForUpdates() {
    const activeSession = localStorage.getItem('jens_sessionId');
    if (this.#initialized && !activeSession) {
      this.close();
    } else if (activeSession) {
      const storedLastUpdate = localStorage.getItem('jens_lastUpdate');
      const lastUpdate = storedLastUpdate ? new Date(storedLastUpdate) : undefined;
      if (
        activeSession !== this.#sessionId ||
        (lastUpdate && (!this.#lastUpdate || lastUpdate > this.#lastUpdate))
      ) {
        this.refreshWidget();
      }
    }
  }

  #handleAnalyticsEvent(event) {
    switch (event.type) {
      // Remember the webchat open/close state in localStorage and load respective settings after page switch/refresh
      case 'webchat/open':
        this.#handleWebchatOpenEvent(event);
        break;
      case 'webchat/outgoing-message':
        this.#handleOutgoingMessageEvent(event);
        break;
      case 'webchat/incoming-message':
        this.#handleIncomingMessageEvent(event);
        break;
    }
  }

  #handleWebchatOpen() {
    this.#patchChatUI();
    // Disable text input if previously disabled
    if (localStorage.getItem('jens_disableInput') === 'true') {
      document.querySelector(
        '[data-cognigy-webchat-root] [data-cognigy-webchat].webchat .webchat-input'
      ).style.display = 'none';
    } else if (localStorage.getItem('jens_disableInput') === 'false') {
      document.querySelector(
        '[data-cognigy-webchat-root] [data-cognigy-webchat].webchat .webchat-input'
      ).style.display = 'initial';
    }
    this.#connectObserver();
  }

  #handleWebchatOpenEvent(_event) {
    if (document.querySelector('[data-cognigy-webchat-root]')) {
      this.#handleWebchatOpen();
    } else {
      const chatRootObserver = new MutationObserver((mutations) => {
        if (!mutations.some((mutation) => mutation.addedNodes.length !== 0)) return;
        if (document.querySelector('[data-cognigy-webchat-root]')) {
          chatRootObserver.disconnect();
          this.#handleWebchatOpen();
        }
      });
      chatRootObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
    }

    localStorage.setItem('jens_chatOpen', true);
    localStorage.setItem('jens_minimized', false);

    // Disable the toggle button when the chat has been opened again
    this.#webchat.updateSettings({
      widgetSettings: {
        disableToggleButton: true
      }
    });
    localStorage.setItem('jens_disableToggle', true);

    // Switch settings to agent and enable file upload if handover still active
    if (localStorage.getItem('jens_handover') === 'true') {
      this.#webchat.updateSettings({
        fileStorageSettings: {
          enabled: true,
          dropZoneText: this.#localization.dropZoneText
        },
        layout: {
          title: this.#localization.agentTitle,
          logoUrl: this.#assets.agentLogoUrl
        }
      });
    }
  }

  static #getCurrentTheme() {
    const currentDate = new Date();
    const theme = Object.entries(CognigyWebchat.THEMES).find((theme) => {
      const [, themeData] = theme;
      return themeData.startDate <= currentDate && currentDate < themeData.endDate;
    });
    return theme?.[0];
  }

  #getLogoUrl() {
    const themeName = CognigyWebchat.#getCurrentTheme();
    return themeName
      ? `${this.getBaseAssetUrl()}/assets/icons/jens-${themeName}.svg`
      : this.#assets.logoUrl;
  }

  #getBotLogoUrl() {
    const themeName = CognigyWebchat.#getCurrentTheme();
    return themeName
      ? `${this.getBaseAssetUrl()}/assets/icons/jens-${themeName}.svg`
      : this.#assets.botLogoUrl;
  }

  #handleIncomingMessageEvent(event) {
    this.#updateLastUpdated();
    const data = event.payload?.data;
    if (data) {
      // Check if webchat settings are being updated
      if ('updateWebchat' in data) {
        this.#webchat.updateSettings(data.updateWebchat);
      }

      // Check if the webchat language is being changed
      if ('changeLanguage' in data) {
        const newLanguage = data.changeLanguage.lang;
        this.#updateLocalization(newLanguage);
      }

      // Check if text input should be disabled
      if (typeof data.disableInput === 'boolean') {
        this.#handleDisableInputEvent(data.disableInput);
      }

      // Check if handover is started or ended
      if (typeof data.handover === 'boolean') {
        this.#handleHandoverEvent(data.handover);
      }

      // Check if Cognigy wants data from Browser Storage
      if ('getValueFromBrowserStorage' in data) {
        this.#handleGetValueFromBrowserStorageEvent(data.getValueFromBrowserStorage);
      }
    }
  }

  #handleGetValueFromBrowserStorageEvent({ storage, key }) {
    if (storage !== 'sessionStorage' && storage !== 'localStorage') {
      console.warn(`Cognigy requested data from an unsupported storage type: ${storage}`);
      this.#webchat.sendMessage('', {
        getValueFromBrowserStorageResult: { error: 'Storage type not supported' }
      });
      return;
    }

    const value = window[storage].getItem(key) || null;
    this.#webchat.sendMessage('', {
      getValueFromBrowserStorageResult: {
        storage,
        key,
        value
      }
    });
  }

  #handleDisableInputEvent(disableInput) {
    document.querySelector(
      '[data-cognigy-webchat-root] [data-cognigy-webchat].webchat .webchat-input'
    ).style.display = disableInput ? 'none' : '';
    localStorage.setItem('jens_disableInput', disableInput);
  }

  #handleHandoverEvent(handover) {
    if (handover) {
      // Switch settings to agent and enable file upload if the session is active
      if (localStorage.getItem('jens_sessionId')) {
        this.#webchat.updateSettings({
          fileStorageSettings: {
            enabled: true,
            dropZoneText: this.#localization.dropZoneText
          },
          layout: {
            title: this.#localization.agentTitle,
            logoUrl: this.#assets.agentLogoUrl
          }
        });
      }
      localStorage.setItem('jens_handover', true);
    } else {
      // Switch settings back to Jens if the session is active
      if (localStorage.getItem('jens_sessionId')) {
        this.#webchat.updateSettings({
          fileStorageSettings: {
            enabled: false
          },
          layout: {
            title: this.#localization.botTitle,
            logoUrl: this.#getBotLogoUrl()
          }
        });
      }
      localStorage.removeItem('jens_handover');
    }
  }

  #handleOutgoingMessageEvent(_event) {
    // Check all outgoing messages
    // Updates the lastMessageDate timestamp with every outgoing message
    this.#lastMessageDate = new Date();
    localStorage.setItem('jens_lastMessageDate', this.#lastMessageDate);
    this.#updateLastUpdated();
  }

  #updateLastUpdated() {
    this.#lastUpdate = new Date();
    localStorage.setItem('jens_lastUpdate', this.#lastUpdate);
  }

  // Check if the privacy policy was accepted (returns true/false)
  #isPrivacyPolicyAccepted() {
    const acceptedTerms = JSON.parse(localStorage.getItem('hasAcceptedTerms'));
    return acceptedTerms?.includes(this.#userId);
  }

  // Get the header bar element
  #getHeaderbar() {
    const headerbarElements = document.getElementsByClassName('webchat-header-bar');
    return headerbarElements.length ? headerbarElements.item(0) : null;
  }

  // Manage the feedback button visibility and creation
  #manageFeedbackButton(headerbar) {
    if (this.#isPrivacyPolicyAccepted()) {
      const feedbackButton = document.getElementById('feedback-button');
      if (feedbackButton) {
        this.#handleExistingFeedbackButton(feedbackButton);
      } else {
        this.#createFeedbackButton(headerbar);
      }
    } else {
      this.#hideBackButton();
    }
  }

  // Handle feedback button visibility (hide/show based on rating widget)
  #handleExistingFeedbackButton(feedbackButton) {
    const ratingWidget = document.getElementsByClassName('webchat-rating-widget-root');
    feedbackButton.style.display = ratingWidget.length ? 'none' : 'flex';
  }

  // Create the feedback button if it doesn't exist
  #createFeedbackButton(headerbar) {
    const feedbackButton = document.createElement('button');
    feedbackButton.type = 'button';
    feedbackButton.id = 'feedback-button';
    feedbackButton.className = 'feedback-button';
    feedbackButton.innerHTML = this.#localization.feedbackLabel;
    feedbackButton.addEventListener('click', this.#feedbackHandler);
    this.#handleExistingFeedbackButton(feedbackButton);
    headerbar.insertBefore(feedbackButton, headerbar.childNodes[0]);
  }

  // Hide the back button if the privacy policy isn't accepted
  #hideBackButton() {
    const backButton = document.getElementsByClassName('webchat-header-back-button')[0];
    if (backButton) {
      backButton.style.visibility = 'hidden';
    }
  }

  manageHeaderButtons() {
    // hook that subclasses can implement
  }

  #clearStorage() {
    localStorage.removeItem('jens_sessionId');
    localStorage.removeItem('jens_lastMessageDate');
    localStorage.removeItem('jens_lastUpdate');
    localStorage.removeItem('jens_handover');
    localStorage.removeItem('jens_chatOpen');
    localStorage.removeItem('jens_disableInput');
    localStorage.removeItem('jens_disableToggle');
    localStorage.removeItem('jens_language');
    localStorage.removeItem('jens_minimized');
    localStorage.removeItem('hasAcceptedTerms');
  }

  #replaceAttachmentLabels() {
    // Get all file attachments
    const elements = document.querySelectorAll('.webchat-media-files-template-root');
    const elementsToUpdate = [...elements].filter(
      (element) => !element.querySelector('.attachment-bubble')
    );
    if (elementsToUpdate.length > 0) {
      this.#observer.disconnect();
      elementsToUpdate.forEach((element) => {
        // Skip this element if the label is already set
        if (element.querySelector('.attachment-bubble')) {
          return;
        }
        // Count attached images and files for each element
        let fileAmount = 0;
        let attachmentMessageLabel = '';
        // Loop through each child of the current element
        for (let child of element.children) {
          // Count attached files
          if (child.classList.contains('webchat-media-template-files-container')) {
            fileAmount += child.children.length;
          }
          // Count attached images
          if (child.classList.contains('webchat-media-template-image-container')) {
            fileAmount += child.children.length;
          }
        }
        // Determine string and phrasing based on the fileAmount
        if (fileAmount >= 2) {
          attachmentMessageLabel =
            fileAmount + ' ' + this.#localization.multipleAttachmentsLabel;
        } else if (fileAmount === 1) {
          attachmentMessageLabel = this.#localization.singleAttachmentLabel;
        }
        // Remove default file attachments for the current element
        element.innerHTML = '';
        // Replace file attachments with a generic message
        const attachmentChip = document.createElement('div');
        attachmentChip.innerHTML = attachmentMessageLabel;
        attachmentChip.classList.add('attachment-bubble');
        element.appendChild(attachmentChip);
      });
      // Reenable observer
      this.#connectObserver();
    }
  }

  #removeScrollButtonContent() {
    if (document.querySelector('.webchat-scroll-to-bottom-button')) {
      document.querySelector('.webchat-scroll-to-bottom-button').textContent = '';
    }
  }

  #connectObserver() {
    this.#observer.observe(
      document.querySelector('[data-cognigy-webchat-root]')?.parentElement,
      {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      }
    );
  }

  #logError(error) {
    if (typeof Sentry !== 'undefined') {
      this.#logErrorToSentry(error);
    }
    if (typeof DD_LOGS !== 'undefined') {
      this.#logErrorToDDLogs(error);
    }
    if (typeof DD_RUM !== 'undefined') {
      this.#logErrorToDDRum(error);
    }
    if (
      typeof Sentry === 'undefined' &&
      typeof DD_LOGS === 'undefined' &&
      typeof DD_RUM === 'undefined'
    ) {
      console.error(error);
    }
  }

  #logErrorToSentry(err) {
    try {
      if (err instanceof Error) {
        Sentry.captureException(err);
      } else {
        Sentry.captureMessage(JSON.stringify(err), 'error');
      }
    } catch (e) {
      //ignore
    }
  }

  #logErrorToDDLogs(err) {
    try {
      if (err instanceof Error) {
        DD_LOGS.logger.error('Cognigy error', {}, err);
      } else {
        DD_LOGS.logger.error(JSON.stringify(err), {});
      }
    } catch (e) {
      //ignore
    }
  }

  #logErrorToDDRum(err) {
    try {
      if (err instanceof Error) {
        DD_RUM.addError(err);
      } else {
        DD_RUM.addError(new Error(JSON.stringify(err)));
      }
    } catch (e) {
      //ignore
    }
  }

  #getState() {
    return this.isActive() ? 'active' : 'inactive';
  }

  #stateChanged() {
    const newState = this.#getState();
    for (const listener of this.#webchatStatelisteners) {
      try {
        listener(newState);
      } catch (err) {
        console.error(err);
      }
    }
  }

  constructor() {
    document.addEventListener('visibilitychange', this.#visibilityListener);
    window.addEventListener('focus', this.#windowFocusListener);

    if (this.isActive()) {
      //reset activity values
      this.#lastMessageDate = new Date(localStorage.getItem('jens_lastMessageDate'));
      this.#lastUpdate = new Date(localStorage.getItem('jens_lastUpdate'));
    } else {
      this.#clearStorage();
    }

    this.#determineNSAppScenarioId();
    this.#startupBehavior = this.defineStartupBehavior();

    if (this.#startupBehavior !== 'SKIP_INIT') {
      if (this.#startupBehavior === 'INIT_MINIMIZED') {
        localStorage.setItem('jens_minimized', 'true');
        localStorage.setItem('jens_disableToggle', 'false');
      }
      this.init();
    }
  }

  init(initialMessage) {
    this.#initializing = true;
    this.#initialized = false;

    // In case LocalStorage is supported...
    if (localStorage) {
      const storedLanguage = localStorage.getItem('jens_language');
      this.#language = storedLanguage || window.CognigyWebchatConfig.lang;
      this.#fetchLanguageResources(this.#language)
        .then((localization) => {
          this.#localization = localization;

          this.#userId = window.localStorage.getItem('jens_userId');
          if (!this.#userId) {
            this.#userId = self.crypto.randomUUID();
            localStorage.setItem('jens_userId', this.#userId);
          }
          // Try to load a previously stored sessionId from the LocalStorage
          this.#sessionId = window.localStorage.getItem('jens_sessionId');
          // In case there was no previously stored sessionId, generate one and store it into LocalStorage
          if (!this.#sessionId) {
            this.#sessionId = 'session-' + crypto.randomUUID();
            this.#lastMessageDate = new Date();
            localStorage.setItem('jens_lastMessageDate', this.#lastMessageDate);
            localStorage.setItem('jens_sessionId', this.#sessionId);
            localStorage.setItem('jens_disableToggle', true);
          }

          // Initializes the webchat with some custom settings
          initWebchat(window.CognigyWebchatConfig.endpoint, {
            userId: this.#userId,
            sessionId: this.#sessionId,
            settings: {
              layout: {
                title: localization.botTitle,
                logoUrl: this.#getLogoUrl(),
                botAvatarName: localization.botAvatarName,
                useOtherAgentLogo: true,
                agentAvatarName: localization.agentAvatarName,
                botLogoUrl: this.#getBotLogoUrl(),
                agentLogoUrl: this.#assets.agentLogoUrl,
                watermark: 'none'
              },
              behavior: {
                inputPlaceholder: localization.inputPlaceholder,
                AIAgentNoticeText: localization.AIAgentNoticeText,
                disableUserTypingEvent: true
              },
              customTranslations: localization.customTranslations,
              startBehavior: this.#getStartBehavior(initialMessage),
              fileAttachmentMaxSize: 41943040,
              privacyNotice: {
                ...localization.privacyNotice,
                url:
                  (window.CognigyWebchatConfig.nsnlLinkBase || '') +
                  localization.privacyNotice.url
              },
              unreadMessages: {
                unreadMessageTitleText: localization.unreadMessageTitleText,
                unreadMessageTitleTextPlural: localization.unreadMessageTitleTextPlural
              },
              widgetSettings: {
                disableToggleButton: JSON.parse(localStorage.getItem('jens_disableToggle'))
              }
            }
          })
            .then(async (webchat) => {
              this.#webchat = webchat;
              window.webchat = webchat;

              // Establish event listener
              this.#webchat.registerAnalyticsService((event) => {
                this.#handleAnalyticsEvent(event);
              });

              if (this.#startupBehavior === 'INIT_OPEN' || this.#openRequested === true) {
                await this.#webchat.open();
                this.#openRequested = false;
              }

              this.#initialized = true;
              this.#initializing = false;
              this.#stateChanged();
            })
            .catch((e) => {
              this.#logError(e);
              this.#initializing = false;
            });
        })
        .catch((e) => {
          this.#logError(e);
          this.#initializing = false;
        });
    } else {
      this.#logError(new Error('Cannot initialize CognigyWebchat, localstorage not supported'));
      this.#initializing = false;
    }
  }

  async open(initialMessage) {
    this.#openRequested = true;
    if (!this.#initializing && !this.#initialized) {
      this.init(initialMessage);
    } else if (this.#initialized) {
      let webchatWindow = document.getElementById('webchatWindow');
      //If webchat already open, give it a little rattle
      if (
        localStorage.getItem('jens_chatOpen') === 'true' &&
        localStorage.getItem('jens_minimized') !== 'true' &&
        webchatWindow
      ) {
        webchatWindow.classList.add('shake');
        setTimeout(() => webchatWindow.classList.remove('shake'), 400);
        webchatWindow.tabIndex = -1;
        webchatWindow.focus();
      } else {
        await this.#webchat.open();
      }
    }
  }

  // Reload the widget when the chat is updated in another tab
  refreshWidget() {
    if (!this.#initializing) {
      try {
        this.#observer.disconnect();
        const webchatRoot = document.querySelector('[data-cognigy-webchat-root="true"]');
        if (webchatRoot) {
          webchatRoot.parentElement.remove();
        }
        this.#lastMessageDate = new Date(localStorage.getItem('jens_lastMessageDate'));
        this.#lastUpdate = new Date(localStorage.getItem('jens_lastUpdate'));
        if (localStorage.getItem('jens_minimized') === 'true') {
          this.#startupBehavior = 'INIT_MINIMIZED';
        } else {
          this.#startupBehavior = 'INIT_OPEN';
        }
        this.init();
      } catch (err) {
        this.#logError(err);
      }
    }
  }

  close() {
    if (this.#initialized) {
      this.#observer.disconnect();
      // Send close event to Cognigy (used by transformer/CXOne logic) NB - input transformer will block this if not in handover
      this.#webchat.sendMessage('', { webchatEvent: 'closed' });
      // Remove webchat from DOM
      const webchatRoot = document.querySelector('[data-cognigy-webchat-root="true"]');
      if (webchatRoot) {
        webchatRoot.parentElement.remove();
      }

      // Also delete other local storage items to reset the session
      this.#clearStorage();
      this.#initializing = false;
      this.#initialized = false;
      this.#lastMessageDate = undefined;
      this.#lastUpdate = undefined;
      this.#sessionId = undefined;
      this.#webchat = undefined;
      this.#localization = undefined;
      this.#language = undefined;
      this.#stateChanged();
    }
  }

  // Request feedback form from Cognigy if the feedback button in the header is clicked
  async openFeedback() {
    try {
      const { endpoint } = window.CognigyWebchatConfig;
      const url = new URL(endpoint);
      const cognigyEndpointBaseUrl = url.origin;
      const cognigyUrlToken = url.pathname.slice(1);

      const response = await fetch(`${cognigyEndpointBaseUrl}/notify/${cognigyUrlToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          userId: this.#userId,
          sessionId: this.#sessionId,
          text: '',
          data: {
            _plugin: {
              type: 'request-rating',
              data: this.#localization.feedbackForm
            }
          },
          URLToken: cognigyUrlToken
        })
      });
      if (!response.ok) {
        const errText = await response.text();
        console.error('Feedback button error:', `HTTP ${response.status}: ${errText}`);
        await this.#webchat.showNotification(this.#localization.feedbackFormError);
      }
    } catch (error) {
      console.error('Feedback button error:', error);
      await this.#webchat.showNotification(this.#localization.feedbackFormError);
    }
  }

  addStateListener(listener) {
    if (typeof listener !== 'function') {
      return;
    }
    this.#webchatStatelisteners.push(listener);
    try {
      listener(this.#getState());
    } catch (err) {
      console.error(err);
    }
    return () => {
      this.#webchatStatelisteners = this.#webchatStatelisteners.filter((fn) => fn !== listener);
    };
  }

  get nsAppScenarioId() {
    return this.#nsAppScenarioId;
  }

  get webchat() {
    return this.#webchat;
  }
}
